import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/invite/${token}`);
        setState({ loading: false, invite: r.data, error: null });
        setName(r.data.name || "");
      } catch (err) {
        setState({ loading: false, invite: null, error: err?.response?.data?.detail || "Invite invalid" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/invite/${token}/accept`, { password: pw1, name: name.trim() });
      setDone(true);
      toast.success("Account activated — please sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  if (state.loading) return <Frame><Loader2 className="animate-spin text-[#f28a16]" size={32} /></Frame>;

  if (state.error) return (
    <Frame>
      <div className="bg-white rounded-md p-6 max-w-md w-full text-center" data-testid="setup-invalid">
        <AlertTriangle size={40} className="mx-auto text-red-600 mb-3" />
        <h1 className="font-display font-black uppercase text-xl text-[#111619] mb-2">Invite unavailable</h1>
        <p className="text-sm text-[#3f4b52]">{state.error}</p>
        <p className="text-xs text-[#3f4b52] mt-3">Please ask Diesel &amp; Dust to send you a new setup link.</p>
        <Link to="/" className="mt-4 inline-block text-[#f28a16] text-sm underline">Return to the website</Link>
      </div>
    </Frame>
  );

  if (done) return (
    <Frame>
      <div className="bg-white rounded-md p-6 max-w-md w-full text-center">
        <ShieldCheck size={40} className="mx-auto text-emerald-600 mb-3" />
        <h1 className="font-display font-black uppercase text-xl text-[#111619] mb-2">Welcome</h1>
        <p className="text-sm text-[#3f4b52]">Your account is active. Redirecting to sign in…</p>
      </div>
    </Frame>
  );

  return (
    <Frame>
      <div className="bg-white rounded-md p-6 max-w-md w-full shadow-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-[#f28a16]/10 text-[#f28a16] px-3 py-1 rounded-sm text-xs font-display uppercase tracking-widest">
            <ShieldCheck size={12} /> Complete your setup
          </div>
          <h1 className="mt-2 font-display font-black uppercase text-2xl text-[#111619]">
            Set your password
          </h1>
          <p className="text-xs text-[#3f4b52] mt-1">
            Signed in as <strong>{state.invite.email}</strong>
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="setup-form">
          <Field label="Your name">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="setup-name"
                   className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <Field label="Choose a password (min 8 chars)">
            <input type="password" required minLength={8} value={pw1} onChange={(e) => setPw1(e.target.value)}
                   data-testid="setup-pw1"
                   className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <Field label="Confirm password">
            <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                   data-testid="setup-pw2"
                   className="w-full h-11 px-3 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <button type="submit" disabled={busy}
                  data-testid="setup-submit"
                  className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
            Activate account
          </button>
        </form>
      </div>
    </Frame>
  );
}

const Frame = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#0f1416] via-[#20282c] to-[#0f1416] flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <div className="flex justify-center mb-6"><Logo variant="group" size="lg" dark /></div>
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">{label}</span>
    {children}
  </label>
);
